import { useState } from 'react';

import useAuth from '../../hooks/useAuth';
import { useChat } from '../../hooks/useChat';
import { useTheme } from '../../hooks/useTheme';
import Avatar from './Avatar';

type View = 'chats' | 'settings' | 'profile' | 'calls' | 'learning' | 'help' | 'voice';

type NavbarProps = {
  view: View;
  onNavigate: (view: View) => void;
};

const Navbar = ({ view, onNavigate }: NavbarProps) => {
  const { user, logout, sessionWarning } = useAuth();
  const { systemStatus, accounts, realtimeConnected, offline } = useChat();
  const { theme, toggle } = useTheme();
  const [loggingOut, setLoggingOut] = useState(false);

  // Resolve WhatsApp status from the active managed account, not the legacy
  // global connector. The legacy connector may be stuck on "connecting" while
  // the actual account is "ready".
  const activeAccount = accounts.find((a) => a.isActive) ?? accounts.find((a) => a.provider === 'local_qr');
  const waState = activeAccount?.status.state ?? systemStatus?.whatsapp.state ?? 'laden';
  const waReason = activeAccount?.status.reason ?? systemStatus?.whatsapp.reason;
  const waReady = waState === 'ready' || waState === 'authenticated';

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <header className="app-header">
      {sessionWarning && (
        <div className="session-warning" role="status">
          {sessionWarning}
        </div>
      )}
      <button
        type="button"
        className="app-brand"
        onClick={() => onNavigate('chats')}
        aria-label="AI MessageBot chatoverzicht"
      >
        <span className="brand-mark small" aria-hidden="true">
          AI
        </span>
        <span>
          <strong>AI MessageBot</strong>
          <small>Secure CRM Assistant</small>
        </span>
      </button>
      <nav className="main-nav" aria-label="Hoofdnavigatie">
        <button
          type="button"
          onClick={() => onNavigate('chats')}
          aria-current={view === 'chats' ? 'page' : undefined}
        >
          Chats
        </button>
        <button
          type="button"
          onClick={() => onNavigate('calls')}
          aria-current={view === 'calls' ? 'page' : undefined}
        >
          Oproepen
        </button>
        <button
          type="button"
          onClick={() => onNavigate('voice')}
          aria-current={view === 'voice' ? 'page' : undefined}
        >
          Voice
        </button>
        <button
          type="button"
          onClick={() => onNavigate('learning')}
          aria-current={view === 'learning' ? 'page' : undefined}
        >
          ATA
        </button>
        <button
          type="button"
          onClick={() => onNavigate('help')}
          aria-current={view === 'help' ? 'page' : undefined}
        >
          Kennisbank
        </button>
        <button
          type="button"
          onClick={() => onNavigate('settings')}
          aria-current={view === 'settings' ? 'page' : undefined}
        >
          Instellingen
        </button>
        <button
          type="button"
          onClick={() => onNavigate('profile')}
          aria-current={view === 'profile' ? 'page' : undefined}
        >
          Profiel
        </button>
      </nav>
      <div className="header-status">
        <button
          type="button"
          className="button icon theme-toggle"
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Licht thema' : 'Donker thema'}
          title={theme === 'dark' ? 'Licht thema' : 'Donker thema'}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
        <span
          className={`status-pill ${waReady ? 'success' : 'warning'}`}
          title={waReason ?? (waReady ? undefined : 'WhatsApp is niet verbonden')}
        >
          <span aria-hidden="true">●</span>
          WhatsApp {waReady ? 'Verbonden' : waState}
        </span>
        <span
          className={`status-pill ${offline || !realtimeConnected ? 'warning' : 'success'}`}
        >
          <span aria-hidden="true">●</span>
          {offline ? 'Offline' : realtimeConnected ? 'Realtime' : 'Verbinden'}
        </span>
      </div>
      <div className="user-menu">
        <Avatar
          src={user?.profile.avatarUrl}
          alt={user?.profile.displayName ?? 'Gebruiker'}
          size={36}
        />
        <span className="user-label">
          <strong>{user?.profile.displayName}</strong>
          <small>{user?.role === 'admin' ? 'Beheerder' : 'Medewerker'}</small>
        </span>
        <button
          type="button"
          className="button icon"
          onClick={() => void handleLogout()}
          disabled={loggingOut}
          aria-label="Uitloggen"
          title="Uitloggen"
        >
          ↪
        </button>
      </div>
    </header>
  );
};

export default Navbar;